import React, { useState } from "react";
import { Clock, Check, Users, Star } from "lucide-react";
import { Link } from "react-router-dom";
import BookingDialog from "./BookingDialog";
import { useAutoTranslate, T } from "../lib/autoTranslate";

const TourCard = ({ tour, featured = false }) => {
  const [open, setOpen] = useState(false);
  const detailUrl = `/tour/${tour.slug || tour.id}`;
  const title = useAutoTranslate(tour.title);
  const description = useAutoTranslate(tour.description);

  if (featured) {
    return (
      <>
        <div className="card grid md:grid-cols-2">
          <Link to={detailUrl} className="relative aspect-[4/3] md:aspect-auto md:min-h-[440px] block group">
            <img src={tour.image} alt={tour.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
            {tour.label && (
              <span className="absolute top-4 left-4 bg-[var(--ri-red)] text-white text-[11px] font-semibold tracking-widest uppercase px-3 py-1.5 rounded-md">
                <T>{tour.label}</T>
              </span>
            )}
            <div className="absolute top-4 right-4 flex items-center gap-2 bg-black/45 backdrop-blur text-white text-[13px] px-3 py-1.5 rounded-full">
              <Clock size={14} /> {tour.schedule}
            </div>
            {tour.images?.length > 1 && (
              <div className="absolute bottom-4 right-4 bg-black/55 backdrop-blur text-white text-[12px] px-2.5 py-1 rounded-full">
                {tour.images.length} photos
              </div>
            )}
          </Link>
          <div className="p-8 md:p-10 flex flex-col">
            <Link to={detailUrl} className="hover:opacity-80 transition-opacity">
              <h3 className="font-serif-display text-[26px] md:text-[32px] leading-[1.1]">
                {title}
              </h3>
            </Link>
            <p className="eyebrow text-[var(--ri-muted)] mt-3">
              {tour.location} · {tour.duration} · {tour.max_guests}
            </p>
            <p className="mt-4 text-[15.5px] leading-[1.7] text-[var(--ri-ink-soft)]">
              {description}
            </p>

            {tour.features?.length > 0 && (
              <ul className="mt-5 grid sm:grid-cols-3 gap-3 text-[13.5px] text-[var(--ri-ink-soft)]">
                {tour.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check size={16} className="text-[var(--ri-red)] mt-0.5" />
                    <span><T>{f}</T></span>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-auto pt-8 flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="font-serif-display text-[28px] leading-none">{tour.price}</div>
                <div className="text-[12px] text-[var(--ri-muted)] mt-1">per person</div>
              </div>
              <div className="flex gap-2">
                <Link to={detailUrl} className="btn-outline text-[13px] py-2 px-4">See details</Link>
                <button onClick={() => setOpen(true)} className="btn-primary">
                  Book now
                </button>
              </div>
            </div>
          </div>
        </div>
        <BookingDialog open={open} onOpenChange={setOpen} tour={tour} />
      </>
    );
  }

  return (
    <>
      <article className="card flex flex-col">
        <Link to={detailUrl} className="relative aspect-[4/3] block group">
          <img src={tour.image} alt={tour.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
          {tour.label && (
            <span className="absolute top-3 left-3 bg-white/95 text-[var(--ri-ink)] text-[11px] font-semibold tracking-widest uppercase px-2.5 py-1 rounded">
              <T>{tour.label}</T>
            </span>
          )}
          <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-black/50 text-white text-[12px] px-2.5 py-1 rounded-full">
            <Clock size={12} /> {tour.schedule}
          </div>
          {tour.images?.length > 1 && (
            <div className="absolute bottom-3 right-3 bg-black/55 backdrop-blur text-white text-[11px] px-2 py-0.5 rounded-full">
              {tour.images.length} photos
            </div>
          )}
        </Link>
        <div className="p-6 flex flex-col flex-1">
          <p className="eyebrow text-[var(--ri-muted)] flex items-center gap-2">
            {tour.location} · {tour.duration}
            <span className="inline-flex items-center gap-1 text-[var(--ri-muted)]">
              <Users size={12} /> {tour.max_guests}
            </span>
          </p>
          <Link to={detailUrl} className="hover:opacity-80 transition-opacity">
            <h3 className="mt-2 font-serif-display text-[22px] leading-[1.15]">{title}</h3>
          </Link>
          {tour.rating && (
            <div className="mt-1.5 flex items-center gap-1.5">
              <Star size={13} className="rating-star" fill="currentColor" />
              <span className="text-[13px] font-semibold">{tour.rating}</span>
              {tour.review_count && (
                <span className="text-[12.5px] text-[var(--ri-muted)]">({tour.review_count.toLocaleString()} reviews)</span>
              )}
            </div>
          )}
          <p className="mt-3 text-[14.5px] leading-[1.65] text-[var(--ri-ink-soft)] line-clamp-4">
            {description}
          </p>
          <div className="mt-auto pt-6 flex items-center justify-between gap-3">
            <div className="font-serif-display text-[22px]">{tour.price}</div>
            <div className="flex gap-2">
              <Link to={detailUrl} className="btn-outline text-[12.5px] py-1.5 px-3">Details</Link>
              <button onClick={() => setOpen(true)} className="btn-primary text-[13px] py-2 px-4">
                Book now
              </button>
            </div>
          </div>
        </div>
      </article>
      <BookingDialog open={open} onOpenChange={setOpen} tour={tour} />
    </>
  );
};

export default TourCard;
